import { Button } from "design-system/atoms/Button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "design-system/atoms/Dialog"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Plus, SearchIcon } from "lucide-react"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { GoalType, GoalTypeEnum, GoalsResponseDataSchema } from "schema"
import { z } from "zod"
import { GoalPickerTable } from "./GoalPickerTable"
import { UpsertGoalModal } from "./UpsertGoalModal"

const ITEMS_PER_PAGE = 10

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectGoal: (goal: z.infer<typeof GoalsResponseDataSchema>[number]) => void
  selectedGoalIds: string[]
}

export function GoalPickerDialog({ open, onOpenChange, onSelectGoal, selectedGoalIds }: Props) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState("")
  const [typeFilter, setTypeFilter] = useState<GoalType | undefined>(undefined)
  const [sortField, setSortField] = useState<"name" | "type" | "createdAt">("name")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const { data: goalsData, isPending } = useGoalsQuery({
    query: {
      query,
      page,
      total: ITEMS_PER_PAGE,
      sortBy: { field: sortField, order: sortOrder },
      filterBy: {
        type: typeFilter,
      },
    },
    onSuccess: (data) => {
      setTotalCount(data.pageInfo.count)
    },
  })

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / ITEMS_PER_PAGE) : 0

  const handleSort = (field: "name" | "type" | "createdAt") => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortOrder("asc")
    }
  }

  return (
    <>
      <Dialog open={open && !showCreateModal} onOpenChange={onOpenChange}>
        <DialogContent size="5xl">
          <DialogHeader>
            <DialogTitle>{t("campaigns.goals.selectGoal")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <SearchQuery
                placeholder={t("campaigns.goalsManagement.search")}
                setQuery={(value) => {
                  setQuery(value)
                  setPage(1)
                }}
                defaultValue={query}
                icon={SearchIcon}
              />
              <Select
                onValueChange={(value) => {
                  setTypeFilter(value === "all" ? undefined : (value as GoalType))
                  setPage(1)
                }}
                value={typeFilter ?? "all"}
              >
                <SelectTrigger isActive={typeFilter !== undefined} className="w-48">
                  <SelectValue placeholder={t("campaigns.goalsManagement.typePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">
                      {t("campaigns.goalsManagement.typePlaceholder")}
                    </SelectItem>
                    <SelectItem value={GoalTypeEnum.Open}>{t("campaigns.goals.open")}</SelectItem>
                    <SelectItem value={GoalTypeEnum.Informative}>
                      {t("campaigns.goals.informative")}
                    </SelectItem>
                    <SelectItem value={GoalTypeEnum.Predefined}>
                      {t("campaigns.goals.predefined")}
                    </SelectItem>
                    <SelectItem value={GoalTypeEnum.Classification}>
                      {t("campaigns.goals.classification")}
                    </SelectItem>
                    <SelectItem value={GoalTypeEnum.NPS}>{t("campaigns.goals.npsLike")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Button onClick={() => setShowCreateModal(true)} variant="default" className="h-full">
                <Plus className="size-4" />
                {t("campaigns.goalsManagement.create")}
              </Button>
            </div>
            <GoalPickerTable
              goals={goalsData}
              isPending={isPending}
              onSelectGoal={onSelectGoal}
              selectedGoalIds={selectedGoalIds}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                count={totalCount}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      {showCreateModal && (
        <UpsertGoalModal
          onClose={() => {
            setShowCreateModal(false)
          }}
        />
      )}
    </>
  )
}
